import React, { useState } from 'react';
import { X, Copy, CheckCircle, Eye, EyeOff, AlertTriangle } from 'lucide-react';

interface TemporaryPasswordModalProps {
    isOpen: boolean;
    onClose: () => void;
    userData: {
        firstName: string;
        lastName: string;
        email: string;
    };
    temporaryPassword: string;
}

const TemporaryPasswordModal: React.FC<TemporaryPasswordModalProps> = ({
    isOpen,
    onClose,
    userData,
    temporaryPassword
}) => {
    const [copied, setCopied] = useState(false);
    const [showPassword, setShowPassword] = useState(true);

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(temporaryPassword);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            console.error('Failed to copy:', err);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-md w-full">
                {/* Header */}
                <div className="flex justify-between items-center p-6 border-b border-gray-200">
                    <div className="flex items-center space-x-3">
                        <div className="p-2 rounded-lg" style={{ backgroundColor: 'rgba(105, 190, 40, 0.1)' }}>
                            <CheckCircle className="w-6 h-6" style={{ color: '#69BE28' }} />
                        </div>
                        <div>
                            <h3 className="text-lg font-semibold text-gray-900">User Created Successfully</h3>
                            <p className="text-sm text-gray-500">Temporary password generated</p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 transition-colors"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Content */}
                <div className="p-6 space-y-4">
                    {/* User Info */}
                    <div className="bg-gray-50 rounded-lg p-4">
                        <p className="text-sm font-medium text-gray-700 mb-2">User Details:</p>
                        <p className="text-sm text-gray-900">
                            <strong>{userData.firstName} {userData.lastName}</strong>
                        </p>
                        <p className="text-sm text-gray-600">{userData.email}</p>
                    </div>

                    {/* Warning Box */}
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                        <div className="flex items-start">
                            <AlertTriangle className="w-5 h-5 text-yellow-600 mr-3 flex-shrink-0 mt-0.5" />
                            <div>
                                <p className="text-sm font-medium text-amber-800 mb-1">
                                    Important Security Notice
                                </p>
                                <p className="text-xs text-amber-700">
                                    This password will only be shown once. Please copy it now and share it securely with the user.
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Password Display */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Temporary Password
                        </label>
                        <div className="relative">
                            <input
                                type={showPassword ? 'text' : 'password'}
                                value={temporaryPassword}
                                readOnly
                                className="w-full px-3 py-3 pr-20 border border-gray-300 rounded-md bg-white font-mono text-sm focus:outline-none focus:ring-2 focus:ring-offset-2"
                                style={{ '--tw-ring-color': 'rgba(105, 190, 40, 0.5)' } as React.CSSProperties}
                            />
                            <div className="absolute right-2 top-1/2 transform -translate-y-1/2 flex space-x-1">
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="p-1.5 text-gray-400 hover:text-gray-600 rounded"
                                    title={showPassword ? 'Hide password' : 'Show password'}
                                >
                                    {showPassword ? (
                                        <EyeOff className="w-4 h-4" />
                                    ) : (
                                        <Eye className="w-4 h-4" />
                                    )}
                                </button>
                                <div className="group relative inline-flex">
                                    <button
                                        type="button"
                                        onClick={handleCopy}
                                        className="p-1.5 text-gray-400 hover:text-gray-600 rounded"
                                    >
                                        {copied ? (
                                            <CheckCircle className="w-4 h-4 text-green-600" />
                                        ) : (
                                            <Copy className="w-4 h-4" />
                                        )}
                                    </button>
                                    <span className="pointer-events-none absolute bottom-full left-1/2 z-50 mb-1.5 -translate-x-1/2 whitespace-nowrap rounded-lg bg-gray-900 px-2 py-1 text-xs text-white opacity-0 shadow-lg transition-opacity group-hover:opacity-100">{copied ? 'Copied!' : 'Copy to clipboard'}</span>
                                </div>
                            </div>
                        </div>
                        {copied && (
                            <p className="mt-1 text-xs text-green-600">
                                ✓ Copied to clipboard
                            </p>
                        )}
                    </div>

                    {/* Instructions */}
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <p className="text-sm font-medium text-blue-900 mb-2">Next Steps:</p>
                        <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
                            <li>Copy the temporary password above</li>
                            <li>Share it securely with the user (avoid email if possible)</li>
                            <li>User must change password on first login</li>
                        </ol>
                    </div>
                </div>

                {/* Footer */}
                <div className="flex justify-end p-6 border-t border-gray-200">
                    <button
                        onClick={onClose}
                        className="px-6 py-2 text-white rounded-md hover:opacity-90 transition-opacity"
                        style={{ backgroundColor: '#69BE28' }}
                    >
                        Done
                    </button>
                </div>
            </div>
        </div>
    );
};

export default TemporaryPasswordModal;